import React, { useMemo, useState } from 'react';
import { SearchIcon } from 'lucide-react';
import { PageHero } from '../components/PageHero';
import { SectionHeading } from '../components/SectionHeading';
import { ServiceCard } from '../components/ServiceCard';
import { CTABand } from '../components/CTABand';
import { images } from '../data/site';
import { services, categoryOrder, categoryBlurbs } from '../data/services';
import type { ServiceCategory } from '../types/service';
type Filter = 'All' | ServiceCategory;
export function Services() {
  const [filter, setFilter] = useState<Filter>('All');
  const [query, setQuery] = useState('');
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return services.filter(s => {
      const matchesCategory = filter === 'All' || s.category === filter;
      if (!matchesCategory) return false;
      if (!q) return true;
      const haystack = [s.name, s.summary, ...s.blocks.flatMap(b => [b.title, b.body ?? '', ...(b.bullets ?? [])])].join(' ').toLowerCase();
      return haystack.includes(q);
    });
  }, [filter, query]);
  const filters: Filter[] = ['All', ...categoryOrder];
  return <>
      <PageHero eyebrow="Services" title="Everything we repair, update and renovate" description="A full-service licensed and insured builder covering remodeling, home systems, exteriors, commercial property and specialty work." crumbs={[{
      label: 'Home',
      to: '/'
    }, {
      label: 'Services'
    }]} image={images.servicesTag} data-id="element-505" data-mp="1fozerycdy0p5" />

      <section className="border-b border-steel-200 bg-white" data-id="element-506" data-mp="1ug8dqy26gyey">
        <div className="mx-auto flex max-w-7xl flex-col gap-4 px-6 py-5 lg:flex-row lg:items-center lg:justify-between" data-id="element-507" data-mp="1zeta2meb6xy4">
          <div className="flex flex-wrap gap-2" role="group" aria-label="Filter services by category" data-id="element-508" data-mp="23czg1s7bp9hz">
            {filters.map(f => <button key={f} type="button" onClick={() => setFilter(f)} aria-pressed={filter === f} className={`rounded-sm border px-4 py-2.5 text-sm font-bold uppercase tracking-wide transition ${filter === f ? 'border-safety bg-safety text-white' : 'border-steel-300 bg-white text-ink-700 hover:border-ink'}`} data-id="element-509" data-mp="91cc9g6gdypg">
                {f}
              </button>)}
          </div>
          <div className="relative lg:w-80" data-id="element-510" data-mp="1qc986q8bbq1y">
            <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-steel-400" aria-hidden="true" data-id="element-511" data-mp="1qbhfpoa0i8fw" />
            <label htmlFor="service-search" className="sr-only" data-id="element-512" data-mp="202vji88zxueu">
              Search services
            </label>
            <input id="service-search" type="search" value={query} onChange={e => setQuery(e.target.value)} placeholder="Search: gutters, water heater, tile…" className="w-full rounded-sm border border-steel-300 bg-white py-3 pl-9 pr-3 text-base text-ink placeholder:text-steel-400 focus:border-safety focus:outline-none focus:ring-2 focus:ring-safety/30" data-id="element-513" data-mp="2e1lixujag66d" />
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-12 md:py-16" data-id="element-514" data-mp="2nn4mlpol2fz0">
        {filter !== 'All' && <p className="mb-6 max-w-2xl text-base leading-relaxed text-steel-500" data-id="element-515" data-mp="29w8812rhrzay">
            {categoryBlurbs[filter]}
          </p>}

        {visible.length === 0 ? <div className="rounded-sm border border-dashed border-steel-300 bg-white p-12 text-center" data-id="element-516" data-mp="200oayd16yukm">
            <h2 className="font-display text-2xl font-bold uppercase tracking-tight text-ink" data-id="element-517" data-mp="24yzmqtb1rf5h">
              No services match “{query}”
            </h2>
            <p className="mt-2 text-sm text-steel-500" data-id="element-518" data-mp="1vxi8lgh79njf">
              Try a different term, or call {''}
              <a href="tel:+12487617595" className="font-semibold text-safety" data-id="element-519" data-mp="1nxlq3tgzguyt">
                248-761-7595
              </a>{' '}
              and describe the project.
            </p>
            <button type="button" onClick={() => {
          setQuery('');
          setFilter('All');
        }} className="mt-5 rounded-sm border-2 border-ink px-5 py-2.5 text-sm font-bold uppercase tracking-wide text-ink transition hover:bg-ink hover:text-white" data-id="element-520" data-mp="1e70tucos82j2">
              Reset filters
            </button>
          </div> : <>
            <p className="mb-6 text-xs font-bold uppercase tracking-[0.2em] text-steel-400" data-id="element-521" data-mp="v9g4l4i3v1xx">
              Showing {visible.length} of {services.length} services
            </p>
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-id="element-522" data-mp="2pmxugy5zgaw4">
              {visible.map(s => <ServiceCard key={s.slug} service={s} data-id="element-523" data-mp="2lkkp4av5eezv" />)}
            </div>
          </>}
      </section>

      <section className="border-t border-steel-200 bg-white" data-id="element-524" data-mp="7c9ytvlz7vrq">
        <div className="mx-auto max-w-7xl px-6 py-14" data-id="element-525" data-mp="cbmmgiko96vd">
          <SectionHeading eyebrow="Reference" title="Full service index" description="Every service we list, grouped by category." data-id="element-526" data-mp="ca1sov66lryk" />
          <div className="mt-8 grid gap-8 sm:grid-cols-2 lg:grid-cols-3" data-id="element-527" data-mp="l3ycw4fscubc">
            {categoryOrder.map(cat => <div key={cat} data-id="element-528" data-mp="3klo8np6yhd1n">
                <h3 className="border-b-2 border-safety pb-2 font-display text-xl font-semibold uppercase tracking-tight text-ink" data-id="element-529" data-mp="3piems6z79ykf">
                  {cat}
                </h3>
                <ul className="mt-3 space-y-1.5 text-sm text-steel-500" data-id="element-530" data-mp="3tmme8bx1lsmc">
                  {services.filter(s => s.category === cat).flatMap(s => s.blocks.flatMap(b => b.bullets ?? [])).filter((v, i, arr) => arr.indexOf(v) === i).map(item => <li key={`${cat}-${item}`} data-id="element-531" data-mp="39ugv10g039x1">{item}</li>)}
                </ul>
              </div>)}
          </div>
        </div>
      </section>

      <CTABand data-id="element-532" data-mp="3jt506ymvzujb" />
    </>;
}